import React from 'react';
import Icon from '../../../components/AppIcon';

const DrillDownPanel = ({ user, match, prediction, settings, onClose }) => {
  if (!user || !match || !prediction) return null;

  const matchSettings = settings?.matchLevel || {};
  const bonusSettings = settings?.bonusPoints || {};

  const categories = [
    {
      key: 'matchWinner',
      label: 'Match Winner',
      icon: 'Trophy',
      predicted: prediction?.winnerId,
      actual: match?.winnerId,
      predictedName: prediction?.winnerName || '—',
      actualName: match?.winnerName || '—',
      basePoints: matchSettings?.matchWinner?.points || 10,
      enabled: matchSettings?.matchWinner?.enabled,
      confidence: prediction?.winnerConfidence,
      confidenceKey: 'winner',
    },
    {
      key: 'tossWinner',
      label: 'Toss Winner',
      icon: 'Coins',
      predicted: prediction?.tossWinnerId,
      actual: match?.tossWinnerId,
      predictedName: prediction?.tossWinnerName || '—',
      actualName: match?.tossWinnerName || '—',
      basePoints: matchSettings?.tossWinner?.points || 5,
      enabled: matchSettings?.tossWinner?.enabled,
    },
    {
      key: 'battingFirst',
      label: 'Batting First',
      icon: 'ArrowUpCircle',
      predicted: prediction?.battingFirstId,
      actual: match?.battingFirstId,
      predictedName: prediction?.battingFirstName || '—',
      actualName: match?.battingFirstName || '—',
      basePoints: matchSettings?.battingFirst?.points || 5,
      enabled: matchSettings?.battingFirst?.enabled,
    },
    {
      key: 'manOfMatch',
      label: 'Man of the Match',
      icon: 'Star',
      predicted: prediction?.manOfMatchId,
      actual: match?.manOfMatchId,
      predictedName: prediction?.manOfMatchName || '—',
      actualName: match?.manOfMatchName || '—',
      basePoints: matchSettings?.manOfMatch?.points || 15,
      enabled: matchSettings?.manOfMatch?.enabled,
      confidence: prediction?.motmConfidence,
      confidenceKey: 'motm',
    },
    {
      key: 'highestRunScorer',
      label: 'Highest Run Scorer',
      icon: 'TrendingUp',
      predicted: prediction?.highestScorerId,
      actual: match?.highestScorerId,
      predictedName: prediction?.highestScorerName || '—',
      actualName: match?.highestScorerName || '—',
      basePoints: matchSettings?.highestRunScorer?.points || 20,
      enabled: matchSettings?.highestRunScorer?.enabled,
      confidence: prediction?.scorerConfidence,
      confidenceKey: 'scorer',
    },
    {
      key: 'highestWicketTaker',
      label: 'Highest Wicket Taker',
      icon: 'Zap',
      predicted: prediction?.highestWicketTakerId,
      actual: match?.highestWicketTakerId,
      predictedName: prediction?.highestWicketTakerName || '—',
      actualName: match?.highestWicketTakerName || '—',
      basePoints: matchSettings?.highestWicketTaker?.points || 20,
      enabled: matchSettings?.highestWicketTaker?.enabled,
      confidence: prediction?.wicketConfidence,
      confidenceKey: 'wicket',
    },
  ];

  const getConfidenceLabel = (val) => {
    if (!val) return null;
    if (val >= 4) return { label: 'High', color: 'text-emerald-600 dark:text-emerald-400' };
    if (val >= 2) return { label: 'Medium', color: 'text-amber-600 dark:text-amber-400' };
    return { label: 'Low', color: 'text-gray-500' };
  };

  const getConfidenceBonus = (conf) => {
    if (!bonusSettings?.confidenceBonus?.enabled || !conf) return 0;
    if (conf >= 4) return bonusSettings?.confidenceBonus?.highConfidence || 10;
    if (conf >= 2) return bonusSettings?.confidenceBonus?.mediumConfidence || 5;
    return 0;
  };

  const rows = categories?.filter(c => c?.enabled)?.map(cat => {
      const hasPrediction = !!cat?.predicted;
      const hasActual = !!cat?.actual;
      const isCorrect = hasPrediction && hasActual && cat?.predicted === cat?.actual;
      const confInfo = getConfidenceLabel(cat?.confidence);
      const confBonus = isCorrect ? getConfidenceBonus(cat?.confidence) : 0;
      const pointsAwarded = isCorrect ? cat?.basePoints + confBonus : 0;

      return { ...cat, hasPrediction, hasActual, isCorrect, confInfo, confBonus, pointsAwarded };
    });

  const totalPoints = rows?.reduce((s, r) => s + r?.pointsAwarded, 0);
  const correctCount = rows?.filter(r => r?.isCorrect)?.length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Points Breakdown</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              <span className="font-medium text-gray-700 dark:text-gray-300">{user?.name}</span>
              <span className="mx-2 text-gray-300 dark:text-gray-600">·</span>
              {match?.team1Name} vs {match?.team2Name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <Icon name="X" size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Summary bar */}
        <div className="flex items-center gap-6 px-6 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
              <Icon name="Award" size={16} className="text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Total Points</p>
              <p className="text-lg font-bold text-primary-600 dark:text-primary-400">{totalPoints}</p>
            </div>
          </div>
          <div className="w-px h-8 bg-gray-200 dark:bg-gray-700" />
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              <Icon name="CheckCircle" size={16} className="text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Correct</p>
              <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{correctCount}/{rows?.length}</p>
            </div>
          </div>
          {bonusSettings?.confidenceBonus?.enabled && (
            <>
              <div className="w-px h-8 bg-gray-200 dark:bg-gray-700" />
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <Icon name="Sparkles" size={16} className="text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Confidence Bonus</p>
                  <p className="text-lg font-bold text-amber-600 dark:text-amber-400">
                    +{rows?.reduce((s, r) => s + r?.confBonus, 0)}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Table */}
        <div className="overflow-y-auto flex-1">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-36">Category</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Predicted</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actual</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Result</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Points</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {rows?.map((row) => (
                <tr
                  key={row?.key}
                  className={`transition-colors ${row?.isCorrect ? 'bg-emerald-50/40 dark:bg-emerald-900/10' : row?.hasPrediction && row?.hasActual ? 'bg-red-50/30 dark:bg-red-900/10' : ''}`}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Icon name={row?.icon} size={15} className="text-gray-400 flex-shrink-0" />
                      <span className="font-medium text-gray-700 dark:text-gray-300 text-xs">{row?.label}</span>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    {row?.hasPrediction ? (
                      <div>
                        <span className="text-gray-800 dark:text-gray-200">{row?.predictedName}</span>
                        {row?.confInfo && (
                          <span className={`ml-2 text-xs ${row?.confInfo?.color}`}>({row?.confInfo?.label})</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400 italic text-xs">No prediction</span>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    {row?.hasActual ? (
                      <span className="text-gray-800 dark:text-gray-200">{row?.actualName}</span>
                    ) : (
                      <span className="text-gray-400 italic text-xs">Not set</span>
                    )}
                  </td>
                  <td className="px-4 py-4 text-center">
                    {!row?.hasPrediction || !row?.hasActual ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 text-xs">
                        <Icon name="Minus" size={10} /> N/A
                      </span>
                    ) : row?.isCorrect ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400 text-xs font-medium">
                        <Icon name="Check" size={10} /> Correct
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 text-xs font-medium">
                        <Icon name="X" size={10} /> Wrong
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {row?.isCorrect ? (
                      <div className="flex flex-col items-end">
                        <span className="font-bold text-emerald-600 dark:text-emerald-400">+{row?.pointsAwarded}</span>
                        {row?.confBonus > 0 && (
                          <span className="text-xs text-amber-500 dark:text-amber-400">
                            ({row?.basePoints} + {row?.confBonus} bonus)
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400">0</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60">
                <td colSpan={4} className="px-6 py-3 text-sm font-semibold text-gray-700 dark:text-gray-300">Total</td>
                <td className="px-6 py-3 text-right text-base font-bold text-primary-600 dark:text-primary-400">+{totalPoints}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DrillDownPanel;
